import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { resetFilter, removeSelected } from '../../store/globalFilterSlice';
import Button from './Button';
import ConfirmationModal from '../ui/alert/ConfirmationModal';
import { useLocation } from "react-router-dom";
import useFetch from "../../hooks/useFetch";


const ITEMS_LIMIT = 40;
const filterKeyMap = {
    country: "country_id",
    store_type: "store_type_id",
    category: "category_id",
    sub_category: "sub_category_id",
    segment: "segment_id",
    form: "form_id",
    manufacture: "manufacture_id",
    brand: "brand_id",
    variant: "variant_id",
    pack_size: "pack_size_id",
    promotion: "promotion_id",
    sku: "sku_id",
};
const sectionNames = [
    "country",
    "store_type",
    "category",
    "sub_category",
    "segment",
    "form",
    "manufacture",
    "brand",
    "variant",
    "pack_size",
    "promotion",
    "sku",
];

const getDisplayName = (display_name) => {
    const names = {
        country: "Country",
        store_type: "Store Type",
        category: "Category",
        sub_category: "Sub Category",
        segment: "Segment",
        form: "Form",
        manufacture: "Manufacture",
        brand: "Brand",
        variant: "Variant",
        pack_size: "Pack Size",
        promotion: "Promotion",
        sku: "SKU",
    };
    return names[display_name] || display_name;
};

const SelectedGlobalFilter = () => {
    const dispatch = useDispatch();
    const { post } = useFetch();
const [errorModalOpen, setErrorModalOpen] = useState(false);
const [errorModalMsg, setErrorModalMsg] = useState("");
    // const { selectedFilters, filterData } = useSelector((state) => state.globalFilter);
        const location = useLocation();
        const isCrossSource = location.pathname === "/ri/cross_source";
    
        // Use the correct filters based on route
        const { selectedFilters, crossSelectedFilters, filterData } = useSelector((state) => state.globalFilter);
        const filtersToShow = isCrossSource ? crossSelectedFilters : selectedFilters;
    
    const [visibleCount, setVisibleCount] = useState(ITEMS_LIMIT);
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingRemove, setPendingRemove] = useState(null);
    const [clearAllModalOpen, setClearAllModalOpen] = useState(false);

    // const formattedSelectedFilters = useMemo(() => {
    //     const formattedFilters = {};
    //     sectionNames.forEach((category) => {
    //         const values = selectedFilters[category];
    //         if (values && values.length > 0) {
    //             formattedFilters[category] = {
    //                 data: filterData[category]?.data
    //                     ? filterData[category].data.filter((item) => values.includes(item.value))
    //                     : [],
    //                 display_name: getDisplayName(filterData[category]?.display_name || category),
    //             };
    //         }
    //     });
    //     return formattedFilters;
    // }, [selectedFilters, filterData]);
const buildValidatePayload = (filters) => ({
    global_filter: {
        date_filter: {}, // Add date filters if needed
        filter: Object.fromEntries(
            Object.entries(filters)
                .filter(([_, values]) => values && values.length > 0)
                .map(([key, values]) => [filterKeyMap[key] || key, values])
        ),
    }
});
const formattedSelectedFilters = useMemo(() => {
    const formattedFilters = {};
    sectionNames.forEach((category) => {
        const values = filtersToShow[category]; // <-- use filtersToShow
        if (values && values.length > 0) {
            formattedFilters[category] = {
                data: filterData[category]?.data
                    ? filterData[category].data.filter((item) => values.includes(item.value))
                    : [],
                display_name: getDisplayName(filterData[category]?.display_name || category),
            };
        }
    });
    return formattedFilters;
}, [filtersToShow, filterData]); // <-- update dependency

    const flattenedFilters = useMemo(() => {
        if (Object.keys(formattedSelectedFilters).length === 0) return [];
        return sectionNames.flatMap((category) => {
            const filter = formattedSelectedFilters[category];
            if (!filter) return [];
            const { data, display_name } = filter;
            return [
                { type: 'category', category, display_name },
                ...data.map((item) => ({ ...item, category })),
            ];
        });
    }, [formattedSelectedFilters]);

    const handleRemove = (category, value, name) => {
        setPendingRemove({ category, value, name });
        setModalOpen(true);
    };

    // const confirmRemove = () => {
    //     if (pendingRemove) {
    //         dispatch(removeSelected({ category: pendingRemove.category, value: pendingRemove.value }));
    //     }
    //     setModalOpen(false);
    //     setPendingRemove(null);
    // };
const filterType = isCrossSource ? 'cross' : 'global';

// const confirmRemove = () => {
//     if (pendingRemove) {
//         dispatch(removeSelected({ category: pendingRemove.category, value: pendingRemove.value, filterType }));
//     }
//     setModalOpen(false);
//     setPendingRemove(null);
// };
const confirmRemove = async () => {
    if (!pendingRemove) return;
    // Prepare new filters after removal
    const newFilters = { ...filtersToShow };
    newFilters[pendingRemove.category] = (newFilters[pendingRemove.category] || []).filter(v => v !== pendingRemove.value);
    if (newFilters[pendingRemove.category].length === 0) delete newFilters[pendingRemove.category];

    // Build payload and endpoint
    const payload = buildValidatePayload(newFilters);
    const endpoint = isCrossSource ? "ri/cross-source-data-validate" : "ri/data-validate";

    try {
        const response = await post(endpoint, payload);
        if (response?.valid) {
            dispatch(removeSelected({ category: pendingRemove.category, value: pendingRemove.value, filterType }));
            setModalOpen(false);
            setPendingRemove(null);
        } else {
            setErrorModalMsg(response?.message || "Data is not available for the selected combination of filters.");
            setErrorModalOpen(true);
        }
    } catch (e) {
        setErrorModalMsg("Something went wrong. Please try again.");
        setErrorModalOpen(true);
    }
};
// const confirmClearAll = () => {
//     dispatch(resetFilter({ filterType }));
//     setClearAllModalOpen(false);
// };
    
const confirmClearAll = async () => {
    // Build payload with empty filters
    const payload = buildValidatePayload({});
    const endpoint = isCrossSource ? "ri/cross-source-data-validate" : "ri/data-validate";

    try {
        const response = await post(endpoint, payload);
        if (response?.valid) {
            dispatch(resetFilter({ filterType }));
            setClearAllModalOpen(false);
        } else {
            setErrorModalMsg(response?.message || "Data is not available for the selected combination of filters.");
            setErrorModalOpen(true);
        }
    } catch (e) {
        setErrorModalMsg("Something went wrong. Please try again.");
        setErrorModalOpen(true);
    }
};

const cancelRemove = () => {
        setModalOpen(false);
        setPendingRemove(null);
    };

    // --- Clear All Confirmation ---
    const handleClearAll = () => {
        setClearAllModalOpen(true);
    };

    // const confirmClearAll = () => {
    //     dispatch(resetFilter());
    //     setClearAllModalOpen(false);
    // };

    const cancelClearAll = () => {
        setClearAllModalOpen(false);
    };

    // -----------------------------

    const handleShowAll = () => setVisibleCount(flattenedFilters.length);
    const handleShowLess = () => setVisibleCount(ITEMS_LIMIT);

    if (flattenedFilters.length === 0) return null;
    const remainingCount = flattenedFilters.length - visibleCount;

    return (
        <div className="selected-filters-bar">
            <div className="selected-filters-bar__chips">
                <ul className="selected-filters-list">
                    {flattenedFilters.slice(0, visibleCount).map((item) =>
                        item.type === 'category' ? (
                            // <li key={`category-${item.category}`} className="selected-filters-list__category">
                            //     <strong>{item.display_name}:</strong>
                            // </li>
                            <li key={`category-${item.category}`} className="selected-filters-list__category">
    <strong>
        {item.display_name
            .toLowerCase()
            .replace(/\b\w/g, c => c.toUpperCase())
        }:
    </strong>
</li>
                        ) : (
                            <li key={`${item.category}-${item.value}`} className="selected-filters-list__chip">
                                <span className="selected-chip">
                                    {item.name}
                                    <button
                                        onClick={() => handleRemove(item.category, item.value, item.name)}
                                        className="selected-chip__remove"
                                        aria-label="Remove"
                                    >
                                        &times;
                                    </button>
                                </span>
                            </li>
                        )
                    )}
                    {remainingCount > 0 ? (
                        <li>
                            <button onClick={handleShowAll} className="badge-btn">
                                +{remainingCount} more
                            </button>
                        </li>
                    ) : visibleCount > ITEMS_LIMIT ? (
                        <li>
                            <button onClick={handleShowLess} className="badge-btn active">
                                Show Less
                            </button>
                        </li>
                    ) : null}
                </ul>
            </div>
            <div className="selected-filters-bar__clear">
                <button
                    className="clear-link"
                    onClick={handleClearAll}
                >
                    Clear all
                </button>
            </div>
            {/* Single value remove confirmation */}
        {/* <ConfirmationModal
    open={modalOpen}
    onClose={cancelRemove}
    onConfirm={confirmRemove}
    filterName={pendingRemove?.name}
    subMessage="To cancel multiple entries, please navigate to the filter section and reset your filters."
/> */}
<ConfirmationModal
    open={modalOpen}
    onClose={cancelRemove}
    onConfirm={confirmRemove}
    filterName={
        pendingRemove?.name
            ? pendingRemove.name
                .toLowerCase()
                .replace(/\b\w/g, c => c.toUpperCase())
            : ""
    }
    subMessage="To cancel multiple entries, please navigate to the filter section and reset your filters."
/>  
            {/* Clear all confirmation */}
      <ConfirmationModal
    open={clearAllModalOpen}
    onClose={cancelClearAll}
    onConfirm={confirmClearAll}
    filterName={null}
    title="Warning"
    message="Are you sure you want to clear all?" // <-- Custom message
    subMessage="" // <-- No sub-message
    confirmText="Clear All" // <-- Button text
    cancelText="Cancel"
/>
<ConfirmationModal
    open={errorModalOpen}
    onClose={() => setErrorModalOpen(false)}
    type="error"
    message={errorModalMsg}
    title="Error"
/>
        </div>
    );
};

export default SelectedGlobalFilter;


// import React, { useState, useMemo } from 'react';
// import { useSelector, useDispatch } from 'react-redux';
// import { resetFilter, removeSelected } from '../../store/globalFilterSlice';
// import Button from './Button';
// import ConfirmationModal from '../ui/alert/ConfirmationModal';

// const ITEMS_LIMIT = 30;

// const sectionNames = [
//     "country",
//     "store_type",
//     "category",
//     "sub_category",
//     "segment",
//     "form",
//     "manufacture",
//     "brand",
//     "variant",
//     "pack_size",
//     "promotion",
//     "sku",
// ];

// const getDisplayName = (display_name) => {
//     const names = {
//         country: "Country",
//         store_type: "Store Type",
//         category: "Category",
//         sub_category: "Sub Category",
//         segment: "Segment",
//         form: "Form",
//         manufacture: "Manufacture",
//         brand: "Brand",
//         variant: "Variant",
//         pack_size: "Pack Size",
//         promotion: "Promotion",
//         sku: "SKU",
//     };
//     return names[display_name] || display_name;
// };

// /**
//  * To open the global filter when clicking "+N more", 
//  * pass an `openFilter` prop from the parent (Header) like:
//  * <SelectedGlobalFilter openFilter={() => setIsFilterOpen(true)} />
//  */
// const SelectedGlobalFilter = ({ openFilter }) => {
//     const dispatch = useDispatch();
//     const { selectedFilters, filterData } = useSelector((state) => state.globalFilter);
//     const [modalOpen, setModalOpen] = useState(false);
//     const [pendingRemove, setPendingRemove] = useState(null);
//     const [clearAllModalOpen, setClearAllModalOpen] = useState(false);

//     const formattedSelectedFilters = useMemo(() => {
//         const formattedFilters = {};
//         sectionNames.forEach((category) => {
//             const values = selectedFilters[category];
//             if (values && values.length > 0) {
//                 formattedFilters[category] = {
//                     data: filterData[category]?.data
//                         ? filterData[category].data.filter((item) => values.includes(item.value))
//                         : [],
//                     display_name: getDisplayName(filterData[category]?.display_name || category),
//                 };
//             }
//         });
//         return formattedFilters;
//     }, [selectedFilters, filterData]);

//     const flattenedFilters = useMemo(() => {
//         if (Object.keys(formattedSelectedFilters).length === 0) return [];
//         return sectionNames.flatMap((category) => {
//             const filter = formattedSelectedFilters[category];
//             if (!filter) return [];
//             const { data, display_name } = filter;
//             return [
//                 { type: 'category', category, display_name },
//                 ...data.map((item) => ({ ...item, category })),
//             ];
//         });
//     }, [formattedSelectedFilters]);

//     const handleRemove = (category, value, name) => {
//         setPendingRemove({ category, value, name });
//         setModalOpen(true);
//     };

//     const confirmRemove = () => {
//         if (pendingRemove) {
//             dispatch(removeSelected({ category: pendingRemove.category, value: pendingRemove.value }));
//         }
//         setModalOpen(false);
//         setPendingRemove(null);
//     };

//     const cancelRemove = () => {
//         setModalOpen(false);
//         setPendingRemove(null);
//     };

//     // --- Clear All Confirmation ---
//     const handleClearAll = () => {
//         setClearAllModalOpen(true);
//     };

//     const confirmClearAll = () => {
//         dispatch(resetFilter());
//         setClearAllModalOpen(false);
//     };

//     const cancelClearAll = () => {
//         setClearAllModalOpen(false);
//     };

//     if (flattenedFilters.length === 0) return null;
//     const remainingCount = flattenedFilters.length - ITEMS_LIMIT;

//     return (
//         <div className="selected-filters-bar">
//             <div className="selected-filters-bar__chips">
//                 <ul className="selected-filters-list">
//                     {flattenedFilters.slice(0, ITEMS_LIMIT).map((item) =>
//                         item.type === 'category' ? (
//                             <li key={`category-${item.category}`} className="selected-filters-list__category">
//                                 <strong>{item.display_name}:</strong>
//                             </li>
//                         ) : (
//                             <li key={`${item.category}-${item.value}`} className="selected-filters-list__chip">
//                                 <span className="selected-chip">
//                                     {item.name}
//                                     <button
//                                         onClick={() => handleRemove(item.category, item.value, item.name)}
//                                         className="selected-chip__remove"
//                                         aria-label="Remove"
//                                     >
//                                         &times;
//                                     </button>
//                                 </span>
//                             </li>
//                         )
//                     )}
//                     {remainingCount > 0 && (
//                         <li>
//                             <button
//                                 onClick={() => {
//                                     if (openFilter) openFilter();
//                                 }}
//                                 className="badge-btn"
//                             >
//                                 +{remainingCount} more
//                             </button>
//                         </li>
//                     )}
//                 </ul>
//             </div>
//             <div className="selected-filters-bar__clear">
//                 <button
//                     className="clear-link"
//                     onClick={handleClearAll}
//                 >
//                     Clear all
//                 </button>
//             </div>
//             {/* Single value remove confirmation */}
//             <ConfirmationModal
//                 open={modalOpen}
//                 onClose={cancelRemove}
//                 onConfirm={confirmRemove}
//                 filterName={pendingRemove?.name}
//             />
//             {/* Clear all confirmation */}
//             <ConfirmationModal
//                 open={clearAllModalOpen}
//                 onClose={cancelClearAll}
//                 onConfirm={confirmClearAll}
//                 filterName={null}
//                 title="Warning"
//                 message="Are you sure you want to clear all filters?"
//                 subMessage="To cancel multiple entries, please navigate to the filter section and reset your filters."
//                 confirmText="Delete"
//                 cancelText="Cancel"
//             />
//         </div>
//     );
// };

// export default SelectedGlobalFilter;